import React, { PropsWithChildren } from 'react';
import Slider, { Settings } from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { IconCircleArrowLeft, IconCircleArrowRight } from '@tabler/icons-react';

// Hardcoded breakpoints to avoid having to wait for CSR to get the CSS var.
export enum Breakpoints {
  sm = 640,
  md = 768,
  lg = 1024,
  xl = 1280,
}

type CarouselInput = {
  settings?: Settings & {
    stacked?: boolean;
    showArrows?: boolean;
  };
} & PropsWithChildren;

export const CarouselContainer: React.FC<
  {
    useCarousel: boolean;
  } & CarouselInput
> = ({ useCarousel, children, ...props }) => {
  return (
    <>
      {useCarousel && <Carousel {...props}>{children}</Carousel>}
      {!useCarousel && <div className="gap-regular mb-4 flex">{children}</div>}
    </>
  );
};

/**
 * Abstract carousel component, use this as a base
 * and create a new component that uses this guy.
 * pass it the slides you want to be slid.
 */
export const Carousel: React.FC<CarouselInput> = ({ children, settings = { stacked: false, showArrows: true } }) => {
  let sliderRef: Slider | null = null;

  const onNextClick = () => {
    sliderRef!.slickNext();
  };

  const onPrevClick = () => {
    sliderRef!.slickPrev();
  };

  const slickSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    showArrows: true,
    responsive: [
      {
        breakpoint: Breakpoints.xl,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: Breakpoints.lg,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: Breakpoints.md,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: Breakpoints.sm,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
    ...settings,
  };

  return (
    <>
      <Slider
        ref={(slider) => {
          sliderRef = slider;
        }}
        {...slickSettings}
        // this one is because the slide has a gap of 4 it needs
        // to have half of that as a negative margin on the outside.
        className={`${settings.stacked ? 'slick-stacked' : ''}`}
      >
        {children}
      </Slider>

      {slickSettings.showArrows && (
        <div className="flex justify-center md:block">
          <span className="flex items-center gap-2 md:items-start">
            <button aria-label="Previous slide" className="cursor-pointer" onClick={onPrevClick}>
              <IconCircleArrowLeft
                className="text-primary hover:text-primary-400 transition-all"
                width={40}
                height={40}
              />
            </button>
            <button aria-label="Next slide" className="cursor-pointer" onClick={onNextClick}>
              <IconCircleArrowRight
                className="text-primary hover:text-primary-400 transition-all"
                width={40}
                height={40}
              />
            </button>
          </span>
        </div>
      )}
    </>
  );
};
